import { useState } from 'react';
import { Star, ThumbsUp, Meh, ThumbsDown, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { supabase } from '@/integrations/supabase/client';

type Step = 'rate' | 'feedback' | 'done';
type Color = 'green' | 'yellow' | 'red';

export default function Reviews() {
  const { mode, dealershipId, userId } = useAuth();
  const { deals, refresh } = useDealershipData();
  const isReal = mode === 'real';

  const [step, setStep] = useState<Step>('rate');
  const [rating, setRating] = useState<Color | null>(null);
  const [notes, setNotes] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const selectedDeal = deals.find(d => d.id === selectedDealId);

  const persist = async (color: Color, noteText: string) => {
    if (!isReal) return true;
    if (!selectedDealId || !selectedDeal) {
      toast.error('Pick a deal to attach this feedback to');
      return false;
    }
    if (!dealershipId) {
      toast.error('Sign-in required');
      return false;
    }
    setSaving(true);
    const { error } = await supabase.from('customer_feedback').insert({
      dealership_id: dealershipId,
      deal_id: selectedDealId,
      rep_id: selectedDeal.rep_id || userId,
      rating_color: color,
      notes: noteText.trim(),
    });
    setSaving(false);
    if (error) {
      toast.error('Failed to save feedback', { description: error.message });
      return false;
    }
    refresh();
    return true;
  };

  const handleRate = async (color: Color) => {
    setRating(color);
    if (color === 'green') {
      const ok = await persist('green', '');
      if (!ok) { setRating(null); return; }
      setStep('done');
      toast.success('Thank you for the great feedback!');
    } else {
      setStep('feedback');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!rating) return;
    const ok = await persist(rating, notes);
    if (!ok) return;
    if (rating === 'yellow') toast.info('Follow-up logged for the rep.');
    else if (rating === 'red') toast.error("Manager has been alerted. We'll make this right.");
    setStep('done');
  };

  const reset = () => { setStep('rate'); setRating(null); setNotes(''); setSelectedDealId(''); };

  const dealPickerNeeded = isReal && step === 'rate';

  return (
    <div className="max-w-md mx-auto space-y-6 pb-20 lg:pb-0 px-4">
      <div className="text-center pt-4">
        <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
          <Star className="h-6 w-6 text-primary" /> Customer Reviews / CSI
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Reputation &amp; CSI protection</p>
      </div>

      {dealPickerNeeded && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Which deal is this feedback for?</CardTitle>
          </CardHeader>
          <CardContent>
            {deals.length === 0 ? (
              <p className="text-xs text-muted-foreground">No deals yet — create a deal first to capture CSI feedback.</p>
            ) : (
              <Select value={selectedDealId} onValueChange={setSelectedDealId}>
                <SelectTrigger><SelectValue placeholder="Select a deal" /></SelectTrigger>
                <SelectContent>
                  {deals.slice(0, 50).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.customer_name} — {d.vehicle}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'rate' && (
        <div className="flex flex-col items-center gap-6 pt-4">
          <p className="text-lg font-medium text-foreground">How was your experience?</p>

          <div className="flex flex-col items-center gap-5">
            <button
              onClick={() => handleRate('green')}
              disabled={saving || (isReal && !selectedDealId)}
              className="w-40 h-40 rounded-full flex flex-col items-center justify-center gap-2 bg-green-500/10 border-4 border-green-500/40 hover:border-green-400 hover:bg-green-500/20 transition-all active:scale-95 shadow-[0_0_30px_rgba(34,197,94,0.15)] hover:shadow-[0_0_40px_rgba(34,197,94,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving && rating === 'green'
                ? <Loader2 className="h-12 w-12 text-green-400 animate-spin" />
                : <ThumbsUp className="h-12 w-12 text-green-400" />}
              <span className="text-lg font-bold text-green-400">Great!</span>
            </button>

            <button
              onClick={() => handleRate('yellow')}
              disabled={saving || (isReal && !selectedDealId)}
              className="w-40 h-40 rounded-full flex flex-col items-center justify-center gap-2 bg-yellow-500/10 border-4 border-yellow-500/40 hover:border-yellow-400 hover:bg-yellow-500/20 transition-all active:scale-95 shadow-[0_0_30px_rgba(234,179,8,0.15)] hover:shadow-[0_0_40px_rgba(234,179,8,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Meh className="h-12 w-12 text-yellow-400" />
              <span className="text-lg font-bold text-yellow-400">Okay</span>
            </button>

            <button
              onClick={() => handleRate('red')}
              disabled={saving || (isReal && !selectedDealId)}
              className="w-40 h-40 rounded-full flex flex-col items-center justify-center gap-2 bg-red-500/10 border-4 border-red-500/40 hover:border-red-400 hover:bg-red-500/20 transition-all active:scale-95 shadow-[0_0_30px_rgba(239,68,68,0.15)] hover:shadow-[0_0_40px_rgba(239,68,68,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ThumbsDown className="h-12 w-12 text-red-400" />
              <span className="text-lg font-bold text-red-400">Needs Work</span>
            </button>
          </div>
        </div>
      )}

      {step === 'feedback' && (
        <Card>
          <CardHeader>
            <CardTitle>{rating === 'yellow' ? 'What could be better?' : "We're sorry. Tell us more."}</CardTitle>
            <CardDescription>{rating === 'red' ? 'A manager will be notified immediately.' : 'Your rep will follow up with you.'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea placeholder="Share your thoughts..." value={notes} onChange={e => setNotes(e.target.value)} rows={4} />
            <Button onClick={handleSubmitFeedback} className="w-full gap-2" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Submitting…' : 'Submit Feedback'}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 'done' && (
        <Card>
          <CardContent className="p-8 text-center">
            {rating === 'green' ? (
              <>
                <ThumbsUp className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Thank You!</h2>
                <p className="text-muted-foreground mb-6">We'd love it if you could share your experience with others.</p>
                <a href="https://g.page/review" target="_blank" rel="noopener noreferrer">
                  <Button className="gap-2"><ExternalLink className="h-4 w-4" /> Leave a Google Review</Button>
                </a>
              </>
            ) : (
              <>
                <Star className="h-16 w-16 text-primary mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Feedback Received</h2>
                <p className="text-muted-foreground">{rating === 'yellow' ? 'Your rep will follow up shortly.' : 'A manager has been notified and will reach out.'}</p>
              </>
            )}
            <Button variant="ghost" onClick={reset} className="mt-4">Submit Another</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-primary/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">CSI Routing Logic</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-400" /> <strong className="text-foreground">Green</strong> → Routes to Google review link</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-yellow-400" /> <strong className="text-foreground">Yellow</strong> → Creates follow-up task for rep</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-400" /> <strong className="text-foreground">Red</strong> → Alerts manager immediately</div>
        </CardContent>
      </Card>
    </div>
  );
}
